import { updateApplicantDocumentsUsecase } from './../../core/usecases/interactors/applicant/update-applicant-documents';
import { combineEpics, ofType } from "redux-observable";
import { of, from, Observable, mergeMap, catchError } from "rxjs";
import { AnyAction } from "redux";
import { RootStateOrAny } from "react-redux";
import moment from "moment";
import dot from "dot-object";
import { data as constants } from "../../constants";

import {
  uploadImageSuccess,
  uploadImageFailure,
  uploadApplicantDocumentsSuccess,
  uploadApplicantDocumentsFailure,
  deleteApplicantDocumentsSuccess,
  deleteApplicantDocumentsFailure,
  addApplicantSuccess,
  addApplicantFailure,
  fetchApplicantsSuccess,
  fetchApplicantsFailure,
  fetchApplicantSuccess,
  fetchApplicantFailure,
  updateApplicantSuccess,
  updateApplicantFailure,
  fetchApplicantRequest,
  fetchTotalApplicantsSuccess,
  fetchTotalApplicantsFailure,
  fetchApplicantsAfterAYearCountSuccess,
  fetchApplicantsAfterAYearCountFailure,
  checkScheduleSuccess,
  checkScheduleFailure,
} from "./actions";
import { succeededRequest, failedRequest } from "../request/action";

import { uploadImageUsecase } from "../../core/usecases/interactors/storage/upload-image";
import {
  addApplicantUsecase,
  fetchApplicantsUsecase,
  fetchApplicantsListUsecase,
  fetchApplicantUsecase,
  fetchTotalApplicantsUsecase,
  updateApplicantUsecase,
  updateTotalNewApplicantsUsecase,
  deleteApplicantDocumentsUsecase,
} from "../../core/usecases/interactors/applicant";

import {
  UPLOAD_IMAGE,
  UPLOAD_APPLICANT_DOCUMENTS,
  DELETE_APPLICANT_DOCUMENTS,
  ADD_APPLICANT_REQUEST,
  FETCH_APPLICANTS_REQUEST,
  FETCH_APPLICANTS_LIST_REQUEST,
  FETCH_APPLICANTS_AFTER_A_YEAR_COUNT,
  FETCH_TOTAL_APPLICANTS_REQUEST,
  FETCH_APPLICANT_REQUEST,
  UPDATE_APPLICANT_REQUEST,
  SEARCH_APPLICANT_REQUEST,
  UPDATE_TOTAL_NEW_APPLICANTS,
  CHECK_SCHEDULE_REQUEST,
} from "./actionTypes";

import {
  FirebaseApplicant,
  NewApplicantList,
} from "../../core/entities/applicant";

import { errorString } from "../../utils/strings";
import { filterFailed } from "../../utils/helpers/filter";
import { checkScheduleUsecase } from "../../core/usecases/interactors/applicant/check-schedule";

export const uploadApplicantImageEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    storageInteractor: { uploadImage },
  }: { storageInteractor: { uploadImage: uploadImageUsecase } }
): Observable<
  ReturnType<typeof uploadImageSuccess> | ReturnType<typeof uploadImageFailure>
> =>
  action$.pipe(
    ofType(UPLOAD_IMAGE),
    mergeMap(({ payload: { file, fileName } }) => {
      return from(uploadImage(file, fileName)).pipe(
        mergeMap((response) => {
          return of(uploadImageSuccess(response));
        }),
        catchError((error) => {
          const errorMessage = errorString(error);
          return of(uploadImageFailure(errorMessage));
        })
      );
    })
  );

export const uploadApplicantDocumentsEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    applicantInteractor: { updateApplicantDocuments },
    storageInteractor: { uploadImage },
  }: {  applicantInteractor: { updateApplicantDocuments: updateApplicantDocumentsUsecase }, storageInteractor: { uploadImage: uploadImageUsecase } }
): Observable<
  ReturnType<typeof uploadApplicantDocumentsSuccess> | ReturnType<typeof uploadApplicantDocumentsFailure>
> =>
  action$.pipe(
    ofType(UPLOAD_APPLICANT_DOCUMENTS),
    mergeMap(({ payload: { id, file, fileName } }) => {
      return from(updateApplicantDocuments(id, file, fileName)).pipe(
        mergeMap((response) => {
          return of(uploadApplicantDocumentsSuccess(response));
        }),
        catchError((error) => {
          const errorMessage = errorString(error);
          return of(uploadApplicantDocumentsFailure(errorMessage));
        })
      );
    })
  );

export const deleteApplicantDocumentsEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    applicantInteractor: { deleteApplicantDocuments },
  }: {  applicantInteractor: { deleteApplicantDocuments: deleteApplicantDocumentsUsecase } }
): Observable<
  ReturnType<typeof deleteApplicantDocumentsSuccess> | ReturnType<typeof deleteApplicantDocumentsFailure>
> =>
  action$.pipe(
    ofType(DELETE_APPLICANT_DOCUMENTS),
    mergeMap(({ payload: { id, document } }) => {
      return from(deleteApplicantDocuments(id, document)).pipe(
        mergeMap(() => {
            return of(deleteApplicantDocumentsSuccess(document));
        }),
        catchError((error) => {
          const errorMessage = errorString(error);
          return of(deleteApplicantDocumentsFailure(errorMessage));
        })
      );
    })
  );
  
export const addApplicantEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    applicantInteractor: { addApplicant },
  }: { applicantInteractor: { addApplicant: addApplicantUsecase } }
): Observable<
  | ReturnType<typeof addApplicantSuccess>
  | ReturnType<typeof addApplicantFailure>
> =>
  action$.pipe(
    ofType(ADD_APPLICANT_REQUEST),
    mergeMap(({ payload: { applicant } }) => {
      return from(addApplicant(applicant)).pipe(
        mergeMap(() => {
          return of(addApplicantSuccess());
        }),
        catchError((error) => {
          let errorMessage = errorString(error);
          if (errorMessage === constants.commonNetworkError) {
            errorMessage = constants.toastNetworkErrorMessage;
          }
          return of(addApplicantFailure(errorMessage));
        })
      );
    })
  );

export const fetchApplicantsEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    applicantInteractor: { fetchApplicants },
  }: {
    applicantInteractor: {
      fetchApplicants: fetchApplicantsUsecase;
    };
  }
): Observable<
  | ReturnType<typeof fetchApplicantsSuccess>
  | ReturnType<typeof fetchApplicantsFailure>
  | ReturnType<typeof succeededRequest>
  | ReturnType<typeof failedRequest>
> =>
  action$.pipe(
    ofType(FETCH_APPLICANTS_REQUEST),
    mergeMap(({ payload: { source, limit, where } }) => {
      let position: Array<any> = [];
      if (source === "database" && where) {
        position = where.shift();
      }
      return from(fetchApplicants(limit, where)).pipe(
        mergeMap((response) => {
          var applicants = [];
          switch (source) {
            case "newApplicant":
              applicants = response
                .filter(
                  (applicant: FirebaseApplicant) =>
                    applicant.applicationStatus.currentStatus ===
                    "New Applicant"
                )
                .map((applicant: FirebaseApplicant, index: number) => {
                  const applicantName = applicant.applicantName
                    ? `${applicant.applicantName.firstName} ${applicant.applicantName.middleName} ${applicant.applicantName.lastName}`
                    : applicant.fullName;
                  return {
                    key: index + 1,
                    id: applicant.id,
                    name: applicantName,
                    position: applicant.positionApplied,
                    contactNumber: applicant.contactNumber,
                    email: applicant.email,
                    dateApplied: `${moment(applicant.createdAt.toDate()).format(
                      "MM/DD/YYYY hh:mm a"
                    )}`,
                    isApplicationRead:
                      applicant.applicationStatus.subStatus === "Read",
                    referredBy: applicant.referredBy,
                  };
                });
              break;
            case "initialInterview":
              applicants = response
                .filter(
                  (applicant: FirebaseApplicant) =>
                    applicant.applicationStatus.currentStatus ===
                    "Initial Interview"
                )
                .map((applicant: FirebaseApplicant, index: number) => {
                  const status =
                    !applicant.applicationStatus?.subStatus ||
                    applicant.applicationStatus?.subStatus === "Read" ||
                    applicant.applicationStatus?.subStatus === "Unread"
                      ? ""
                      : applicant.applicationStatus?.subStatus;
                  const applicantName = applicant.applicantName
                    ? `${applicant.applicantName.firstName} ${applicant.applicantName.middleName} ${applicant.applicantName.lastName}`
                    : applicant.fullName;
                  return {
                    key: applicant.id,
                    fullName: applicantName,
                    position: applicant.positionApplied,
                    dateMoved: applicant.applicationStatus.dateMoved
                      ? `${moment(
                          applicant.applicationStatus.dateMoved.toDate()
                        ).format("MM/DD/YYYY hh:mm a")}`
                      : null,
                    phase: {
                      name: "Initial Interview",
                      interviewDate: applicant.scheduleDate?.initialInterview
                        ? `${moment(
                            applicant.scheduleDate?.initialInterview.toDate()
                          ).format("MM/DD/YYYY hh:mm a")}`
                        : null,
                      status: status || "Unassigned",
                    },
                    referredBy: applicant.referredBy,
                    applicationStatus: applicant.applicationStatus,
                    scheduleDate: applicant.scheduleDate,
                    interviewers: applicant.interviewers,
                  };
                });
              break;
            case "shortlisted":
              applicants = response
                .filter(
                  (applicant: FirebaseApplicant) =>
                    applicant.applicationStatus.currentStatus === "Shortlisted"
                )
                .map((applicant: FirebaseApplicant, index: number) => {
                  const status =
                    !applicant.applicationStatus?.subStatus ||
                    applicant.applicationStatus?.subStatus === "Read" ||
                    applicant.applicationStatus?.subStatus === "Unread"
                      ? ""
                      : applicant.applicationStatus?.subStatus;
                  const applicantName = applicant.applicantName
                    ? `${applicant.applicantName.firstName} ${applicant.applicantName.middleName} ${applicant.applicantName.lastName}`
                    : applicant.fullName;
                  return {
                    key: applicant.id,
                    fullName: applicantName,
                    position: applicant.positionApplied,
                    contactNumber: applicant.contactNumber,
                    email: applicant.email,
                    dateApplied: `${moment(applicant.createdAt.toDate()).format(
                      "MM/DD/YYYY hh:mm a"
                    )}`,
                    phase: {
                      status: status || "Unassigned",
                    },
                    referredBy: applicant.referredBy,
                  };
                });
              break;
            case "finalInterview":
              applicants = response
                .filter(
                  (applicant: FirebaseApplicant) =>
                    applicant.applicationStatus.currentStatus ===
                    "Final Interview"
                )
                .map((applicant: FirebaseApplicant, index: number) => {
                  const status =
                    !applicant.applicationStatus?.subStatus ||
                    applicant.applicationStatus?.subStatus === "Read" ||
                    applicant.applicationStatus?.subStatus === "Unread"
                      ? ""
                      : applicant.applicationStatus?.subStatus;
                  const applicantName = applicant.applicantName
                    ? `${applicant.applicantName.firstName} ${applicant.applicantName.middleName} ${applicant.applicantName.lastName}`
                    : applicant.fullName;
                  return {
                    key: applicant.id,
                    fullName: applicantName,
                    position: applicant.positionApplied,
                    dateMoved: applicant.applicationStatus.dateMoved
                      ? `${moment(
                          applicant.applicationStatus.dateMoved.toDate()
                        ).format("MM/DD/YYYY hh:mm a")}`
                      : null,
                    phase: {
                      name: "Initial Interview",
                      interviewDate: applicant.scheduleDate?.finalInterview
                        ? `${moment(
                            applicant.scheduleDate?.finalInterview.toDate()
                          ).format("MM/DD/YYYY hh:mm a")}`
                        : null,
                      status: status || "Unassigned",
                    },
                    referredBy: applicant.referredBy,
                    applicationStatus: applicant.applicationStatus,
                    scheduleDate: applicant.scheduleDate,
                    interviewers: applicant.interviewers,
                  };
                });
              break;
            case "technical":
              applicants = response
                .filter(
                  (applicant: FirebaseApplicant) =>
                    applicant.applicationStatus.currentStatus ===
                    "Technical Exam"
                )
                .map((applicant: FirebaseApplicant, index: number) => {
                  const status =
                    !applicant.applicationStatus?.subStatus ||
                    applicant.applicationStatus?.subStatus === "Read" ||
                    applicant.applicationStatus?.subStatus === "Unread"
                      ? ""
                      : applicant.applicationStatus?.subStatus;
                  const scheduleDate = applicant.scheduleDate;
                  const applicantName = applicant.applicantName
                    ? `${applicant.applicantName.firstName} ${applicant.applicantName.middleName} ${applicant.applicantName.lastName}`
                    : applicant.fullName;
                  return {
                    key: applicant.id,
                    fullName: applicantName,
                    position: applicant.positionApplied,
                    dateMoved: applicant.applicationStatus.dateMoved
                      ? `${moment(
                          applicant.applicationStatus.dateMoved.toDate()
                        ).format("MM/DD/YYYY hh:mm a")}`
                      : null,
                    phase: {
                      startDate: scheduleDate?.technicalExam?.startDate
                        ? `${moment(
                            scheduleDate?.technicalExam?.startDate.toDate()
                          ).format("MM/DD/YYYY hh:mm a")}`
                        : null,
                      endDate: scheduleDate?.technicalExam?.endDate
                        ? `${moment(
                            scheduleDate?.technicalExam?.endDate.toDate()
                          ).format("MM/DD/YYYY hh:mm a")}`
                        : null,
                      status: status || "Unassigned",
                    },
                    referredBy: applicant.referredBy,
                  };
                });
              break;
            case "psychological":
              applicants = response
                .filter(
                  (applicant: FirebaseApplicant) =>
                    applicant.applicationStatus.currentStatus ===
                    "Psychological Exam"
                )
                .map((applicant: FirebaseApplicant, index: number) => {
                  const status =
                    !applicant.applicationStatus?.subStatus ||
                    applicant.applicationStatus?.subStatus === "Read" ||
                    applicant.applicationStatus?.subStatus === "Unread"
                      ? ""
                      : applicant.applicationStatus?.subStatus;
                  const scheduleDate = applicant.scheduleDate;
                  const applicantName = applicant.applicantName
                    ? `${applicant.applicantName.firstName} ${applicant.applicantName.middleName} ${applicant.applicantName.lastName}`
                    : applicant.fullName;
                  return {
                    key: applicant.id,
                    fullName: applicantName,
                    position: applicant.positionApplied,
                    dateMoved: applicant.applicationStatus.dateMoved
                      ? `${moment(
                          applicant.applicationStatus.dateMoved.toDate()
                        ).format("MM/DD/YYYY hh:mm a")}`
                      : null,
                    applicationStatus: {
                      psychologicalExamDate: scheduleDate?.psychologicalExam
                        ? `${moment(
                            scheduleDate?.psychologicalExam?.toDate()
                          ).format("MM/DD/YYYY hh:mm a")}`
                        : null,
                      status: status || "Unassigned",
                    },
                    referredBy: applicant.referredBy,
                  };
                });
              break;
            case "jobOffer":
              applicants = response
                .filter(
                  (applicant: FirebaseApplicant) =>
                    applicant.applicationStatus.currentStatus === "Job Offer"
                )
                .map((applicant: FirebaseApplicant) => {
                  const status =
                    !applicant.applicationStatus?.subStatus ||
                    applicant.applicationStatus?.subStatus === "Read" ||
                    applicant.applicationStatus?.subStatus === "Unread"
                      ? ""
                      : applicant.applicationStatus?.subStatus;
                  const scheduleDate = applicant.scheduleDate;
                  const applicantName = applicant.applicantName
                    ? `${applicant.applicantName.firstName} ${applicant.applicantName.middleName} ${applicant.applicantName.lastName}`
                    : applicant.fullName;
                  return {
                    key: applicant.id,
                    fullName: applicantName,
                    position: applicant.positionApplied,
                    dateMoved: applicant.applicationStatus.dateMoved
                      ? `${moment(
                          applicant.applicationStatus.dateMoved.toDate()
                        ).format("MM/DD/YYYY hh:mm a")}`
                      : null,
                    applicationStatus: {
                      jobOfferDate: scheduleDate?.jobOffer
                        ? `${moment(scheduleDate?.jobOffer?.toDate()).format(
                            "MM/DD/YYYY hh:mm a"
                          )}`
                        : null,
                      status: status || "Unassigned",
                    },
                    referredBy: applicant.referredBy,
                    interviewers: applicant.interviewers,
                  };
                });
              break;
            case "database":
              applicants = response
                .filter((applicant: FirebaseApplicant) => {
                  return position[2].includes(applicant.positionApplied);
                })
                .map((applicant: FirebaseApplicant) => {
                  return {
                    name: `${
                      applicant.applicantName.firstName +
                      " " +
                      applicant.applicantName.middleName +
                      " " +
                      applicant.applicantName.lastName
                    }`,
                    linkedinUrl: applicant.linkedinUrl,
                    contactNumber: applicant.contactNumber,
                    email: applicant.email,
                    timeToContact: `${applicant.contactTimeFrom} - ${applicant.contactTimeTo}`,
                    resume: applicant.resume,
                    createdAt: `${moment(applicant.createdAt.toDate()).format(
                      "MM/DD/YYYY hh:mm a"
                    )}`,
                    skills: applicant.skills,
                    platform: applicant.platformApplied,
                    salary: applicant.expectedSalary,
                    recruitmentStatus:
                      applicant.applicationStatus.currentStatus,
                    positionApplied: applicant.positionApplied,
                  };
                });
              break;
            case "failedNewApplicant":
              applicants = filterFailed(response, source);
              break;
            case "failedShortlisted":
              applicants = filterFailed(response, source);
              break;
            case "failedTechnical":
              applicants = filterFailed(response, source);
              break;
            case "failedInitial":
              applicants = filterFailed(response, source);
              break;
            case "failedPsychological":
              applicants = filterFailed(response, source);
              break;
            case "failedFinal":
              applicants = filterFailed(response, source);
              break;
            default:
              applicants = response.map((applicant) => {
                const {
                  applicationStatus,
                  platformApplied,
                  ...otherApplicantValues
                } = applicant;

                const {
                  dateFailed,
                  finalInterviewPassedDate,
                  initialInterviewPassedDate,
                  psychologicalPassedDate,
                  shortlistedPassedDate,
                  technicalPassedDate,
                  ...otherApplicationStatusValues
                } = applicationStatus;

                return {
                  applicationStatus: {
                    dateFailed: dateFailed
                      ? `${moment(dateFailed.toDate()).format(
                          "MM/DD/YYYY hh:mm a"
                        )}`
                      : null,
                    finalInterviewPassedDate: finalInterviewPassedDate
                      ? `${moment(finalInterviewPassedDate.toDate()).format(
                          "MM/DD/YYYY hh:mm a"
                        )}`
                      : null,
                    initialInterviewPassedDate: initialInterviewPassedDate
                      ? `${moment(initialInterviewPassedDate.toDate()).format(
                          "MM/DD/YYYY hh:mm a"
                        )}`
                      : null,
                    psychologicalPassedDate: psychologicalPassedDate
                      ? `${moment(psychologicalPassedDate.toDate()).format(
                          "MM/DD/YYYY hh:mm a"
                        )}`
                      : null,
                    shortlistedPassedDate: shortlistedPassedDate
                      ? `${moment(shortlistedPassedDate.toDate()).format(
                          "MM/DD/YYYY hh:mm a"
                        )}`
                      : null,
                    technicalPassedDate: technicalPassedDate
                      ? `${moment(technicalPassedDate.toDate()).format(
                          "MM/DD/YYYY hh:mm a"
                        )}`
                      : null,
                    ...otherApplicationStatusValues,
                  },
                  platformApplied,
                  ...otherApplicantValues,
                };
              });
              break;
          }

          return of(
            fetchApplicantsSuccess(
              applicants as NewApplicantList[] | FirebaseApplicant[]
            ),
            succeededRequest(FETCH_APPLICANTS_REQUEST)
          );
        }),
        catchError((error) => {
          const errorMessage = errorString(error);
          errorMessage === constants.commonNetworkError &&
            window.location.reload();
          return of(
            fetchApplicantsFailure(errorMessage),
            failedRequest(FETCH_APPLICANTS_REQUEST, errorMessage)
          );
        })
      );
    })
  );

export const fetchApplicantEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    applicantInteractor,
  }: {
    applicantInteractor: {
      fetchApplicant: fetchApplicantUsecase;
    };
  }
) =>
  action$.pipe(
    ofType(FETCH_APPLICANT_REQUEST),
    mergeMap(({ payload: { id } }) => {
      return from(applicantInteractor.fetchApplicant(id)).pipe(
        mergeMap((response) => {
          return of(fetchApplicantSuccess(response));
        }),
        catchError((error) => {
          error
            .toString()
            .includes(
              "Failed to get document because the client is offline."
            ) && window.location.reload();
          const errorMessage = errorString(error);
          return of(fetchApplicantFailure(errorMessage));
        })
      );
    })
  );

export const updateApplicantEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    applicantInteractor,
  }: {
    applicantInteractor: {
      updateApplicant: updateApplicantUsecase;
    };
  }
) =>
  action$.pipe(
    ofType(UPDATE_APPLICANT_REQUEST),
    mergeMap(({ payload: { id, data } }) => {
      return from(applicantInteractor.updateApplicant(id, data)).pipe(
        mergeMap(() => {
          if (dot.object({ ...data }).hasOwnProperty("scheduleDate")) {
            return of(
              updateApplicantSuccess(),
              succeededRequest(UPDATE_APPLICANT_REQUEST, data)
            );
          }
          return of(
            fetchApplicantRequest(id),
            updateApplicantSuccess(),
            succeededRequest(UPDATE_APPLICANT_REQUEST, data)
          );
        }),
        catchError((error) => {
          let errorMessage = errorString(error);
          if (errorMessage.includes(constants.commonNetworkError)) {
            errorMessage = constants.toastNetworkErrorMessage;
          }
          return of(
            updateApplicantFailure(errorMessage),
            failedRequest(UPDATE_APPLICANT_REQUEST, errorMessage)
          );
        })
      );
    })
  );

export const checkScheduleEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    applicantInteractor,
  }: {
    applicantInteractor: {
      checkSchedule: checkScheduleUsecase;
    };
  }
) =>
  action$.pipe(
    ofType(CHECK_SCHEDULE_REQUEST),
    mergeMap(({ payload: { id, dateAndTime, data } }) => {
      return from(
        applicantInteractor.checkSchedule(id, dateAndTime, data)
      ).pipe(
        mergeMap(() => {
          return of(
            checkScheduleSuccess(false),
            succeededRequest(CHECK_SCHEDULE_REQUEST, data)
          );
        }),
        catchError((error) => {
          let errorMessage = errorString(error);
          if (errorMessage.includes("409")) {
            return of(
              checkScheduleSuccess(true),
              succeededRequest(CHECK_SCHEDULE_REQUEST, data)
            );
          }
          if (errorMessage.includes(constants.commonNetworkError)) {
            errorMessage = constants.toastNetworkErrorMessage;
          }
          return of(
            checkScheduleFailure(errorMessage),
            failedRequest(CHECK_SCHEDULE_REQUEST, errorMessage)
          );
        })
      );
    })
  );

export const searchApplicantEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    applicantInteractor: { fetchApplicants },
  }: {
    applicantInteractor: {
      fetchApplicants: fetchApplicantsUsecase;
    };
  }
): Observable<
  ReturnType<typeof succeededRequest> | ReturnType<typeof failedRequest>
> =>
  action$.pipe(
    ofType(SEARCH_APPLICANT_REQUEST),
    mergeMap(({ payload: { limit, where } }) => {
      return from(fetchApplicants(limit, where)).pipe(
        mergeMap((response) => {
          const applicants = response
            .sort((a, b) =>
              moment(a.createdAt).valueOf() > moment(b.createdAt).valueOf()
                ? 1
                : -1
            )
            .map((applicant: FirebaseApplicant) => {
              const applicantName = applicant.applicantName
                ? `${applicant.applicantName.firstName} ${applicant.applicantName.middleName} ${applicant.applicantName.lastName}`
                : applicant.fullName;
              return {
                key: applicant.id,
                fullName: applicantName,
                position: applicant.positionApplied,
                email: applicant.email,
                status: applicant.applicationStatus.currentStatus,
                referredBy: applicant.referredBy,
              };
            });
          const data = {
            keyword: where[0][2],
            applicants,
          };
          return of(succeededRequest(SEARCH_APPLICANT_REQUEST, data));
        }),
        catchError((error) => {
          const errorMessage = errorString(error);
          errorMessage === constants.commonNetworkError &&
            window.location.reload();
          return of(failedRequest(SEARCH_APPLICANT_REQUEST, errorMessage));
        })
      );
    })
  );

export const fetchApplicantsListEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    applicantInteractor: { fetchApplicantsList, fetchTotalApplicants },
  }: {
    applicantInteractor: {
      fetchApplicantsList: fetchApplicantsListUsecase;
      fetchTotalApplicants: fetchTotalApplicantsUsecase;
    };
  }
): Observable<
  ReturnType<typeof succeededRequest> | ReturnType<typeof failedRequest>
> =>
  action$.pipe(
    ofType(FETCH_APPLICANTS_LIST_REQUEST),
    mergeMap(({ payload: { query } }) => {
      return from(fetchApplicantsList(query)).pipe(
        mergeMap((response) => {
          return of(
            succeededRequest(FETCH_APPLICANTS_LIST_REQUEST, {
              ...response,
              query,
            })
          );
        }),
        catchError((error) => {
          const errorMessage = errorString(error);
          errorMessage === constants.commonNetworkError &&
            window.location.reload();
          return of(failedRequest(FETCH_APPLICANTS_LIST_REQUEST, errorMessage));
        })
      );
    })
  );

export const fetchApplicantsAfterAYearEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    applicantInteractor: { fetchApplicantsList },
  }: {
    applicantInteractor: {
      fetchApplicantsList: fetchApplicantsListUsecase;
    };
  }
): Observable<
  | ReturnType<typeof fetchApplicantsAfterAYearCountSuccess>
  | ReturnType<typeof fetchApplicantsAfterAYearCountFailure>
> =>
  action$.pipe(
    ofType(FETCH_APPLICANTS_AFTER_A_YEAR_COUNT),
    mergeMap(({ payload: { query, status } }) => {
      return from(fetchApplicantsList(query)).pipe(
        mergeMap((response) => {
          return of(
            fetchApplicantsAfterAYearCountSuccess(
              response?.data?.length || 0,
              status
            )
          );
        }),
        catchError((error) => {
          const errorMessage = errorString(error);
          errorMessage === constants.commonNetworkError &&
            window.location.reload();
          return of(fetchApplicantsAfterAYearCountFailure(errorMessage));
        })
      );
    })
  );

export const fetchTotalApplicantsEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    applicantInteractor: { fetchTotalApplicants },
  }: {
    applicantInteractor: {
      fetchTotalApplicants: fetchTotalApplicantsUsecase;
    };
  }
): Observable<
  | ReturnType<typeof fetchTotalApplicantsSuccess>
  | ReturnType<typeof fetchTotalApplicantsFailure>
> =>
  action$.pipe(
    ofType(FETCH_TOTAL_APPLICANTS_REQUEST),
    mergeMap(() => {
      return from(fetchTotalApplicants()).pipe(
        mergeMap((response) => {
          return of(fetchTotalApplicantsSuccess(response));
        }),
        catchError((error) => {
          const errorMessage = errorString(error);
          errorMessage === constants.commonNetworkError &&
            window.location.reload();
          return of(fetchTotalApplicantsFailure(errorMessage));
        })
      );
    })
  );

export const updateTotalNewApplicantsEpic = (
  action$: Observable<AnyAction>,
  _state$: RootStateOrAny,
  {
    applicantInteractor,
  }: {
    applicantInteractor: {
      updateTotalNewApplicants: updateTotalNewApplicantsUsecase;
    };
  }
) =>
  action$.pipe(
    ofType(UPDATE_TOTAL_NEW_APPLICANTS),
    mergeMap(({ payload: { currentPhase, prevPhase, currentStatus } }) => {
      return from(
        applicantInteractor.updateTotalNewApplicants(
          currentPhase,
          prevPhase,
          currentStatus
        )
      ).pipe(
        mergeMap(() => {
          return of(succeededRequest(UPDATE_TOTAL_NEW_APPLICANTS));
        }),
        catchError((error) => {
          let errorMessage = errorString(error);
          if (errorMessage.includes(constants.commonNetworkError)) {
            errorMessage = constants.toastNetworkErrorMessage;
          }
          return of(failedRequest(UPDATE_TOTAL_NEW_APPLICANTS, errorMessage));
        })
      );
    })
  );

export default combineEpics(
  fetchApplicantsEpic,
  //@ts-ignore
  addApplicantEpic,
  fetchApplicantEpic,
  updateApplicantEpic,
  searchApplicantEpic,
  fetchApplicantsListEpic,
  fetchApplicantsAfterAYearEpic,
  searchApplicantEpic,
  fetchTotalApplicantsEpic,
  updateTotalNewApplicantsEpic,
  uploadApplicantDocumentsEpic,
  deleteApplicantDocumentsEpic,
  checkScheduleEpic,
);
